import { animate } from "motion";
import {
	calculatePercentage,
	clampNumber,
	debounceRevalidateUserOnlineInfo,
	getPrivateMessageFromError,
	getPublicMessageFromError,
	mutateUserCredits
} from "~/solidJs/shared/helpers";
import {
	collectNodeDataForTask,
	constructNodeStateChangeTracker,
	createNodePullingMachine,
	getTipComputingText,
	getWaitingText,
	prepeareCalculatedParamsForNode,
	prepeareFileParamsForTask,
	sendConnectedNodes,
	TASK_TRACK_STAGES
} from "~/solidJs/shared/helpers/nodeHelpers";
import { PromiseQueue } from "~/solidJs/shared/helpers/queue";
import { TaskStatuses } from "~/solidJs/shared/helpers/types";
import { mutateHistory } from "~/solidJs/widgets/HistoryWidget/helpers/prepareNewHistoryItems";
import Editor from "../editor";
import NodeExecutionTask from "../network/node-task";
import { ExecutionStatus, NodeExecutor } from "../nodes/node-executor";
import { NodeEvents } from "../types/IEventorListners";
import EventAnalytics from "./EventAnalytics";

/**
 * @param {NodeExecutor} executor
 * @param {string} message
 * @param {NodeType} node
 * @param {NodeJS.Timer=} interval
 * @param {number|string=} taskID
 * @param {string=} errorCode
 */
export const onError = (executor, message, _node, interval, _taskID, errorCode) => {
	executor.updateStatus(ExecutionStatus.Error, message, errorCode);
	executor.DOMStyle.executeBlock.finish();
	if (interval) clearInterval(interval);
};

/**
 *
 * @param {AbortController} controller
 * @param {()=>void} onAbort
 */
const checkIsAborted = (controller, onAbort) => {
	if (!controller.signal.aborted) return false;
	onAbort?.();
	return true;
};

export const Default_Executor_Text = "Click to start creating";

/**
 * @param {NodeType} node
 */
export const executor = (node) => {
	const executor = new NodeExecutor(node);
	executor.useEnterExitTriggers();
	executor.setStatusEnabled(true);

	executor.updateStatus(ExecutionStatus.Ready, Default_Executor_Text);
	executor.setExecuteFunction((controller) => execute(node, executor, controller));
};

const createTaskQueue = new PromiseQueue();

/**
 * @param {NodeType} node
 * @param {NodeExecutor} executor
 * @param {AbortController} cancelController
 * @param {Record<string, IFileType>} rewriteValues
 * @returns
 */
export const execute = async (node, executor, cancelController, rewriteValues = {}) => {
	const medianTime = node.definition.medianTime * 1000;
	const executionStartTs = Date.now();
	const waitingText = getWaitingText();
	node.eventor.invoke("preExecutePreparing");
	if (checkIsAborted(cancelController)) return;
	node.closeStyleWidget();
	EventAnalytics.executeNode(node.globalId, executionStartTs);
	executor.DOMStyle.executeBlock.start();

	const { tracker, setTaskId } = constructNodeStateChangeTracker({
		globalId: node.globalId
	});

	try {
		executor.updateStatus(ExecutionStatus.Starting, "Collecting node data...");

		let jsonData;

		try {
			jsonData = await collectNodeDataForTask(node, executor, rewriteValues);
		} catch (e) {
			executor.updateStatus(ExecutionStatus.Error, e, TaskStatuses.Error_params);
			EventAnalytics.sendErrorInfo("error-validate", {
				message: getPrivateMessageFromError(e),
				errorMessage: getPrivateMessageFromError(e),
				globalId: node.globalId
			});
			throw e;
		}

		tracker.trackStateChange({ newState: TASK_TRACK_STAGES.validateParams });
		if (checkIsAborted(cancelController)) return;

		executor.updateStatus(ExecutionStatus.Starting, "Sync with storage...");

		await prepeareFileParamsForTask(jsonData, executor, node);
		node.meta.taskSchema = jsonData;
		tracker.trackStateChange({ newState: TASK_TRACK_STAGES.pushFiles });

		if (checkIsAborted(cancelController)) return;

		executor.updateStatus(ExecutionStatus.Starting, "Creating server task...");

		const clearTaskOnCancel = () => {};
		if (checkIsAborted(cancelController, clearTaskOnCancel)) return;

		let taskID;
		let interval;
		let startTime;

		const startShowCalculatingStatus = () => {
			if (checkIsAborted(cancelController)) return;
			if (interval) clearInterval(interval);
			executor.setQueuedState(false);
			executor.updateStatus(ExecutionStatus.Computing, getTipComputingText());
			executor.setProgress(0);
			startTime = startTime ? startTime : Date.now();
			interval = setInterval(() => {
				if (checkIsAborted(cancelController, () => clearInterval(interval))) return;
				const diff = Date.now() - startTime;
				if (diff >= medianTime) {
					clearInterval(interval);
					return;
				}
				const percentage = calculatePercentage(0, medianTime, diff);
				executor.setProgress(percentage);
			}, 1000);
		};

		executor.updateStatus(ExecutionStatus.Starting, waitingText);

		const onTimeoutBoundary = (message) => {
			executor.updateStatus(ExecutionStatus.Error, message, "error_exec_timeout");
			executor.DOMStyle.executeBlock.finish();
			NodeExecutionTask.stopTasksAsync([node]);
			if (interval) clearInterval(interval);
		};

		const onErrorRetry = () => {
			if (checkIsAborted(cancelController)) return;
			clearInterval(interval);
			let times = 0;
			executor.updateStatus(
				ExecutionStatus.Error,
				`Server connection error. Your node is probably being calculated at the moment. Please wait 80 seconds while we are reaching out to the server. Trying to reconnect: ${times} /80s`,
				"error"
			);
			interval = setInterval(() => {
				if (checkIsAborted(cancelController) || times > 80) {
					clearInterval(interval);
					return;
				}
				times++;
				executor.updateStatus(
					ExecutionStatus.Error,
					`Server connection error. Your node is probably being calculated at the moment. Please wait 80 seconds while we are reaching out to the server. Trying to reconnect: ${times} /80s`,
					"error"
				);
			}, 1000);

			return interval;
		};

		const onDownloadStart = () => {
			executor.updateStatus(ExecutionStatus.Computing, "Downloading result...");
			clearInterval(interval);
		};

		const onPendingStatus = (_queuePos) => {
			if (interval) clearInterval(interval);
			executor.setQueuedState(true);
			executor.updateStatus(ExecutionStatus.Starting, waitingText);
		};

		const onTaskCreated = (taskId) => {
			const price = node.getTaskPrice();
			if (price?.price) {
				mutateUserCredits(-price.price);
				debounceRevalidateUserOnlineInfo();
			}
			sendConnectedNodes(taskId, node);
			taskID = taskId;
			node.taskID = taskId;
			node.meta.taskID = taskId;
			setTaskId(taskId);
			tracker.trackStateChange({ newState: TASK_TRACK_STAGES.taskCreated });
		};

		const onWaitingForLaunch = () => {
			executor.setQueuedState(true);
			executor.updateStatus(ExecutionStatus.Starting, waitingText);
		};

		let resultOutputs;

		try {
			resultOutputs = await createNodePullingMachine(
				node.globalId,
				startShowCalculatingStatus,
				onWaitingForLaunch,
				onTaskCreated,
				onTimeoutBoundary,
				onPendingStatus,
				(e, errorCode) =>
					onError(
						executor,
						getPublicMessageFromError(e) || "Unknown execution error",
						node,
						interval,
						0,
						errorCode
					),
				onErrorRetry,
				jsonData,
				{
					computing: node.averageTimeInSeconds * 1000,
					pending: node.averageTimeInSeconds * 2000,
					prepearing: 900000
				},
				executionStartTs,
				cancelController,
				{
					createTask: (data, _) =>
						createTaskQueue.pushTask(
							() => node.definition.createTaskImpl(data, cancelController.signal),
							cancelController.signal
						),
					getTaskStatus: node.definition.getTaskStatusImpl,
					tracker: tracker
				}
			);
			mutateHistory([resultOutputs]);
		} catch (e) {
			clearInterval(interval);
			if (cancelController.signal.aborted) return;
			throw e;
		} finally {
			Editor.context.graph.revalidateDisability();
		}

		tracker.trackStateChange({ newState: TASK_TRACK_STAGES.done });
		clearInterval(interval);

		resultOutputs = prepeareCalculatedParamsForNode(resultOutputs, node.globalId, 0);

		const ellapsedDiff = Date.now() - startTime;
		if (ellapsedDiff < medianTime) {
			const endPercentage = calculatePercentage(0, medianTime, ellapsedDiff);
			const needToAddToPercentage = 100 - endPercentage;
			await animate(endPercentage, 100, {
				duration: clampNumber(10 * (needToAddToPercentage / 100), 1, 2),
				ease: "linear",
				onUpdate: (animProgress) => {
					executor.setProgress(animProgress);
					return animProgress;
				}
			});
		}

		onDownloadStart();
		resultOutputs = await resultOutputs;
		tracker.trackStateChange({ newState: TASK_TRACK_STAGES.pullFiles });
		node.updateOutputs(resultOutputs);

		await executor.socketTriggeringAsync(executor.triggerSockets.out.exit);

		executor.updateStatus(ExecutionStatus.Done, "Successful");
		const executeNodeSuccessTs = Date.now();
		EventAnalytics.executeNodeSuccess(
			node.globalId,
			executeNodeSuccessTs,
			taskID,
			executeNodeSuccessTs - executionStartTs
		);
		tracker.trackStateChange({ newState: TASK_TRACK_STAGES.success });
		node.eventor.invoke(NodeEvents.SuccessfullExecute);
	} catch (err) {
		tracker.trackStateChange({ newState: TASK_TRACK_STAGES.exception });
		console.error(err);

		if (executor.getLastStatusText() !== err) {
			let errorCode = "error";
			if (
				err &&
				typeof err === "object" &&
				"errorCode" in err &&
				typeof err.errorCode === "string"
			) {
				errorCode = err.errorCode;
			}
			executor.updateStatus(ExecutionStatus.Error, err, errorCode);
		}

		const executeNodeErrorTs = Date.now();
		EventAnalytics.sendErrorInfo("execute-node-error", {
			message: getPrivateMessageFromError(err),
			errorMessage: getPrivateMessageFromError(err),
			globalId: node.globalId,
			executionTs: executeNodeErrorTs,
			...(node.taskID ? { taskId: node.taskID } : {}),
			executionTsDelta: executeNodeErrorTs - executionStartTs
		});
	} finally {
		tracker.trackStateChange({ newState: TASK_TRACK_STAGES.finaly });
	}

	executor.DOMStyle.executeBlock.finish();
};
